'use client'

import { cn } from '@tebuto/lib/utils'
import { type Dispatch, type JSX, type KeyboardEvent, type SetStateAction } from 'react'

export interface SuggestionItem {
    id: string
    label: string
    /** Secondary text rendered after the label, e.g. `80331 München` */
    detail?: string
}

interface SuggestionDropdownProps {
    id: string
    items: SuggestionItem[]
    activeIndex: number
    onSelect: (item: SuggestionItem, index: number) => void
    onActiveIndexChange: (index: number) => void
    isWidget?: boolean
}

/**
 * Shared suggestion listbox rendered absolutely below an input. The input keeps
 * the focus; items are selected on mousedown (before blur) or via keyboard through
 * {@link handleSuggestionKeyDown}.
 */
export default function SuggestionDropdown({
    id,
    items,
    activeIndex,
    onSelect,
    onActiveIndexChange,
    isWidget = false
}: SuggestionDropdownProps): JSX.Element | null {
    if (items.length === 0) {
        return null
    }

    return (
        <ul
            id={id}
            role="listbox"
            aria-label="Vorschläge"
            className={cn(
                'absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg text-sm py-1',
                isWidget ? 'widget-suggestion-list widget-border' : 'border-secondary bg-white'
            )}
        >
            {items.map((item, index) => (
                <li
                    key={item.id}
                    role="option"
                    aria-selected={index === activeIndex}
                    tabIndex={-1}
                    className={cn(
                        'cursor-pointer px-3 py-2 flex items-baseline gap-2',
                        isWidget
                            ? cn('widget-suggestion-item', index === activeIndex && 'widget-suggestion-item--active')
                            : cn('text-foreground-tertiary', index === activeIndex && 'bg-primary-50')
                    )}
                    onMouseDown={(event) => {
                        // Prevent the input blur so the selection handler runs first
                        event.preventDefault()
                        onSelect(item, index)
                    }}
                    onMouseEnter={() => onActiveIndexChange(index)}
                >
                    <span className="truncate">{item.label}</span>
                    {item.detail ? (
                        <span
                            className={cn(
                                'truncate text-xs',
                                isWidget ? 'widget-suggestion-item__detail' : 'text-foreground-muted'
                            )}
                        >
                            {item.detail}
                        </span>
                    ) : null}
                </li>
            ))}
        </ul>
    )
}

interface SuggestionKeyboardOptions {
    open: boolean
    items: SuggestionItem[]
    activeIndex: number
    setActiveIndex: Dispatch<SetStateAction<number>>
    onSelect: (item: SuggestionItem, index: number) => void
    onClose: () => void
}

/**
 * Keyboard handling for an input with an attached {@link SuggestionDropdown}:
 * up/down move the active item, enter selects it, escape closes the dropdown.
 */
export function handleSuggestionKeyDown(
    event: KeyboardEvent<HTMLInputElement>,
    { open, items, activeIndex, setActiveIndex, onSelect, onClose }: SuggestionKeyboardOptions
): void {
    if (!open || items.length === 0) {
        return
    }

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            setActiveIndex((index) => (index + 1) % items.length)
            break
        case 'ArrowUp':
            event.preventDefault()
            setActiveIndex((index) => (index - 1 + items.length) % items.length)
            break
        case 'Enter':
            if (activeIndex >= 0 && activeIndex < items.length) {
                event.preventDefault()
                onSelect(items[activeIndex], activeIndex)
            }
            break
        case 'Escape':
            event.preventDefault()
            onClose()
            break
    }
}
